import { inject, Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { ManageMembershipFeePayload, MembershipFee } from '@models';
import { map, shareReplay, switchMap } from 'rxjs';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class MembershipFeesService {
  private readonly firestore = inject(FirestoreService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly functions = inject(Functions);

  public all$ = this.tenantContext.tenantId$.pipe(
    switchMap(() => this.firestore.listDocuments<MembershipFee>(this.col)),
    shareReplay(1),
  );

  public active$ = this.all$.pipe(map((fees) => fees.filter((f) => f.active)));

  private get col() {
    return this.tenantContext.collections.MEMBERSHIP_FEES;
  }

  create(data: Omit<MembershipFee, 'id'>) {
    const callable = httpsCallable<
      ManageMembershipFeePayload,
      { success: boolean; id: string }
    >(this.functions, 'manageMembershipFee');
    return callable({ action: 'create', data });
  }

  update(feeId: string, data: Partial<Omit<MembershipFee, 'id'>>) {
    const callable = httpsCallable<
      ManageMembershipFeePayload,
      { success: boolean; id: string }
    >(this.functions, 'manageMembershipFee');
    return callable({ action: 'update', feeId, data });
  }

  delete(feeId: string) {
    const callable = httpsCallable<
      ManageMembershipFeePayload,
      { success: boolean; id: string }
    >(this.functions, 'manageMembershipFee');
    return callable({ action: 'delete', feeId });
  }
}
